import React, { useContext, useState, useEffect } from "react";
import { useTheme } from "@material-ui/core/styles";
import { 
  Box, 
  Container, 
  Typography, 
  Card, 
  CardContent, 
  IconButton, 
  Paper, 
  Tab, 
  Tabs,
  Divider,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import {
  SaveAlt,
  Groups,
  Call as CallIcon,
  HourglassEmpty as HourglassEmptyIcon,
  CheckCircle as CheckCircleIcon,
  RecordVoiceOver as RecordVoiceOverIcon,
  GroupAdd as GroupAddIcon,
} from "@mui/icons-material";
import * as XLSX from 'xlsx';
import { toast } from "react-toastify";
import { isArray, isEmpty } from "lodash";
import moment from "moment";
import TableAttendantsStatus from "../../components/Dashboard/TableAttendantsStatus";
import { AuthContext } from "../../context/Auth/AuthContext";
import useDashboard from "../../hooks/useDashboard";
import useContacts from "../../hooks/useContacts";
import useMessages from "../../hooks/useMessages";
import { ChatsUser } from "./ChartsUser";
import ChartDonut from "./ChartDonut";
import { ChartsDate } from "./ChartsDate";
import StatCard from "./StatCard";
import DonutWithLegend from "./DonutWithLegend";
import PeriodFilter from "./PeriodFilter";
import ForbiddenPage from "../../components/ForbiddenPage";
import { i18n } from "../../translate/i18n";
import { dashboardColors, isDashboardDark } from "./dashboardTheme";

const surfaceCardSx = (theme) => ({
  borderRadius: 3,
  border: theme.palette.type === "dark"
    ? "1px solid rgba(148,163,184,0.12)"
    : "1px solid rgba(15,23,42,0.08)",
  boxShadow: theme.palette.type === "dark"
    ? "0 4px 16px rgba(0,0,0,0.24)"
    : "0 4px 12px rgba(15,23,42,0.06)",
  bgcolor: theme.palette.type === "dark" ? "#1e293b" : "#ffffff",
});

const Dashboard = () => {
  const theme = useTheme();
  const pageBackground = theme.palette.fancyBackground;
  const cardSx = surfaceCardSx(theme);
  const [counters, setCounters] = useState({});
  const [attendants, setAttendants] = useState([]);
  const [showFilter, setShowFilter] = useState(false);
  const [dateStartTicket, setDateStartTicket] = useState(moment().startOf('month').format("YYYY-MM-DD"));
  const [dateEndTicket, setDateEndTicket] = useState(moment().format("YYYY-MM-DD"));
  const [fetchDataFilter, setFetchDataFilter] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [activePeriod, setActivePeriod] = useState("month");
  
  const { find } = useDashboard();
  const { user } = useContext(AuthContext);

  let newDate = new Date();
  let date = newDate.getDate();
  let month = newDate.getMonth() + 1;
  let year = newDate.getFullYear();
  let nowIni = `${year}-${month < 10 ? `0${month}` : `${month}`}-01`;
  let now = `${year}-${month < 10 ? `0${month}` : `${month}`}-${date < 10 ? `0${date}` : `${date}`}`;

  useEffect(() => {
    async function firstLoad() {
      await fetchData();
    }
    setTimeout(() => {
      firstLoad();
    }, 1000);
  }, [fetchDataFilter]);

  async function fetchData() {
    setLoading(true);
    let params = {};
    if (!isEmpty(dateStartTicket) && moment(dateStartTicket).isValid()) {
      params = { ...params, date_from: moment(dateStartTicket).format("YYYY-MM-DD") };
    }
    if (!isEmpty(dateEndTicket) && moment(dateEndTicket).isValid()) {
      params = { ...params, date_to: moment(dateEndTicket).format("YYYY-MM-DD") };
    }
    if (Object.keys(params).length === 0) {
      toast.error(i18n.t("dashboard.toasts.configureFilter"));
      setLoading(false);
      return;
    }
    try {
      const data = await find(params);
      setCounters(data.counters);
      if (isArray(data.attendants)) {
        setAttendants(data.attendants);
      } else {
        setAttendants([]);
      }
    } catch (err) {
      console.log("Error loading dashboard:", err);
    } finally {
      setLoading(false);
    }
  }

  const exportarGridParaExcel = () => {
    const ws = XLSX.utils.table_to_sheet(document.getElementById('grid-attendants'));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'RelatorioDeAtendentes');
    XLSX.writeFile(wb, 'relatorio-de-atendentes.xlsx');
  };

  function formatTime(minutes) {
    return moment().startOf("day").add(minutes, "minutes").format("HH[h] mm[m]");
  }

  const GetUsers = () => {
    let userOnline = 0;
    attendants.forEach(user => {
      if (user.online === true) {
        userOnline = userOnline + 1;
      }
    });
    return userOnline;
  };

  const GetContacts = (all) => {
    let props = all ? {} : { dateStart: dateStartTicket, dateEnd: dateEndTicket };
    const { count } = useContacts(props);
    return count;
  };

  const GetMessages = (all, fromMe) => {
    let props = all
      ? { fromMe }
      : { fromMe, dateStart: dateStartTicket, dateEnd: dateEndTicket };
    const { count } = useMessages(props);
    return count;
  };

  const applyPeriod = (period) => {
    const today = moment();
    let start;
    const end = today.format("YYYY-MM-DD");

    if (period === "today") {
      start = end;
    } else if (period === "7d") {
      start = today.clone().subtract(6, "days").format("YYYY-MM-DD");
    } else if (period === "30d") {
      start = today.clone().subtract(29, "days").format("YYYY-MM-DD");
    } else {
      start = today.clone().startOf("month").format("YYYY-MM-DD");
    }

    setActivePeriod(period);
    setDateStartTicket(start);
    setDateEndTicket(end);
    setFetchDataFilter((prev) => !prev);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (user.profile === "user" && user.showDashboard === "disabled") {
    return <ForbiddenPage />;
  }

  const statCards = [
    {
      title: i18n.t("dashboard.cards.inAttendance"),
      value: counters.supportHappening || 0,
      icon: <CallIcon />,
      color: "#3598dc"
    },
    {
      title: i18n.t("dashboard.cards.waiting"),
      value: counters.supportPending || 0,
      icon: <HourglassEmptyIcon />,
      color: "#32c5d2"
    },
    {
      title: i18n.t("dashboard.cards.finalized"),
      value: counters.supportFinished || 0,
      icon: <CheckCircleIcon />,
      color: "#26c281"
    },
    {
      title: i18n.t("dashboard.cards.groups"),
      value: counters.supportGroups || 0,
      icon: <Groups />,
      color: "#8e44ad"
    },
    {
      title: i18n.t("dashboard.cards.activeAttendants"),
      value: `${GetUsers() || 0}/${attendants.length || 0}`,
      icon: <RecordVoiceOverIcon />,
      color: "#e7505a"
    },
    {
      title: i18n.t("dashboard.cards.newContacts"),
      value: counters.leads || 0,
      icon: <GroupAddIcon />,
      color: "#f39c12"
    }
  ];

  const isDark = isDashboardDark(theme);
  const colors = dashboardColors(isDark);

  const statusChartData = [
    { name: i18n.t("dashboard.cards.inAttendance"), value: counters.supportHappening || 0, color: "#2563eb" },
    { name: i18n.t("dashboard.cards.waiting"), value: counters.supportPending || 0, color: "#f59e0b" },
    { name: i18n.t("dashboard.cards.finalized"), value: counters.supportFinished || 0, color: "#10b981" },
  ];

  const activityChartData = [
    { name: i18n.t("dashboard.cards.groups"), value: counters.supportGroups || 0, color: "#8b5cf6" },
    { name: i18n.t("dashboard.cards.newContacts"), value: counters.leads || 0, color: "#f97316" },
    { name: i18n.t("dashboard.assessments.totalCalls"), value: counters.tickets || 0, color: "#0ea5e9" },
  ];

  const npsChartData = [
    { name: i18n.t("dashboard.assessments.prosecutors"), value: parseInt(counters.npsPromotersPerc || 0, 10), color: "#22c55e" },
    { name: i18n.t("dashboard.assessments.neutral"), value: parseInt(counters.npsPassivePerc || 0, 10), color: "#eab308" },
    { name: i18n.t("dashboard.assessments.detractors"), value: parseInt(counters.npsDetractorsPerc || 0, 10), color: "#ef4444" },
  ];

  return (
    <Box sx={{ backgroundColor: pageBackground, minHeight: "100%", width: "100%", py: 2.5 }}>
      <Container maxWidth="xl">
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3, flexWrap: "wrap", gap: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight={700} sx={{ letterSpacing: "-0.02em", color: colors.text }}>
              {`Hola, ${user?.name || "Admin"}!`}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, color: colors.textSoft }}>
              {i18n.t("dashboard.title") || "Dashboard"}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5 }}>
            <PeriodFilter activePeriod={activePeriod} onChange={applyPeriod} />
          </Box>
        </Box>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {statCards.map((card, index) => (
            <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
              <StatCard
                title={card.title}
                value={card.value}
                icon={card.icon}
                color={card.color}
                isDark={isDark}
              />
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2.5, height: "100%", ...cardSx }}>
              <DonutWithLegend title="Por estado" data={statusChartData} />
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2.5, height: "100%", ...cardSx }}>
              <DonutWithLegend title="Actividad" data={activityChartData} />
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2.5, height: "100%", ...cardSx }}>
              <DonutWithLegend title="NPS (%)" data={npsChartData} />
            </Paper>
          </Grid>
        </Grid>

        {/* Tabs Navigation */}
        <Paper sx={{ mb: 3, ...cardSx, overflow: "hidden" }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              minHeight: 48,
              "& .MuiTab-root": { textTransform: "none", fontWeight: 600, fontSize: "0.9rem" },
            }}
          >
            <Tab label={i18n.t("dashboard.tabs.performance")} />
            <Tab label={i18n.t("dashboard.tabs.assessments")} />
            <Tab label={i18n.t("dashboard.tabs.attendants")} />
          </Tabs>
        </Paper>

        {/* Tab Panels */}
        {/* Performance Tab */}
        {activeTab === 0 && (
          <Paper sx={{ p: 3, ...cardSx }}>
            <Typography variant="h6" fontWeight={700} gutterBottom sx={{ color: colors.text }}>
              {i18n.t("dashboard.charts.performance")}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <ChartsDate />
          </Paper>
        )}

        {/* Assessments Tab - NPS Data */}
        {activeTab === 1 && (
          <Paper sx={{ p: 3, ...cardSx }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6" fontWeight={700} sx={{ color: colors.text }}>
                {i18n.t("dashboard.tabs.assessments")}
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />
            
            <Grid container spacing={3}>
              {/* Main NPS Score */}
              <Grid item xs={12} md={3}>
                <Card sx={{ height: "100%", ...cardSx }}>
                  <CardContent>
                    <ChartDonut
                      data={[
                        `{'name': 'Promotores', 'value': ${counters.npsPromotersPerc || 100}}`,
                        `{'name': 'Detratores', 'value': ${counters.npsDetractorsPerc || 0}}`,
                        `{'name': 'Neutros', 'value': ${counters.npsPassivePerc || 0}}`
                      ]}
                      value={counters.npsScore || 0}
                      title="Score"
                      color={(parseInt(counters.npsPromotersPerc || 0) + parseInt(counters.npsDetractorsPerc || 0) + parseInt(counters.npsPassivePerc || 0)) === 0 ? ["#918F94"] : ["#2EA85A", "#F73A2C", "#F7EC2C"]}
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* Promoters */}
              <Grid item xs={12} md={3}>
                <Card sx={{ height: "100%", ...cardSx }}>
                  <CardContent>
                    <ChartDonut
                      title={i18n.t("dashboard.assessments.prosecutors")}
                      value={counters.npsPromotersPerc || 0}
                      data={[`{'name': 'Promotores', 'value': 100}`]}
                      color={["#2EA85A"]}
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* Neutral */}
              <Grid item xs={12} md={3}>
                <Card sx={{ height: "100%", ...cardSx }}>
                  <CardContent>
                    <ChartDonut
                      data={[`{'name': 'Neutros', 'value': 100}`]}
                      title={i18n.t("dashboard.assessments.neutral")}
                      value={counters.npsPassivePerc || 0}
                      color={["#F7EC2C"]}
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* Detractors */}
              <Grid item xs={12} md={3}>
                <Card sx={{ height: "100%", ...cardSx }}>
                  <CardContent>
                    <ChartDonut
                      data={[`{'name': 'Detratores', 'value': 100}`]}
                      title={i18n.t("dashboard.assessments.detractors")}
                      value={counters.npsDetractorsPerc || 0}
                      color={["#F73A2C"]}
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* Assessment Summary */}
              <Grid item xs={12}>
                <Card sx={{ ...cardSx }}>
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <Box sx={{ textAlign: "center", p: 2 }}>
                          <Typography variant="body2" gutterBottom sx={{ color: colors.textSoft }}>
                            {i18n.t("dashboard.assessments.totalCalls")}
                          </Typography>
                          <Typography variant="h4" fontWeight="bold" color="primary">
                            {counters.tickets || 0}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Box sx={{ textAlign: "center", p: 2 }}>
                          <Typography variant="body2" gutterBottom sx={{ color: colors.textSoft }}>
                            {i18n.t("dashboard.assessments.ratedCalls")}
                          </Typography>
                          <Typography variant="h4" fontWeight="bold" color="primary">
                            {counters.withRating || 0}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Box sx={{ textAlign: "center", p: 2 }}>
                          <Typography variant="body2" gutterBottom sx={{ color: colors.textSoft }}>
                            {i18n.t("dashboard.assessments.evaluationIndex")}
                          </Typography>
                          <Typography variant="h4" fontWeight="bold" color="primary">
                            {Number(counters.percRating / 100 || 0).toLocaleString(undefined, { style: 'percent' })}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Attendants Tab */}
        {activeTab === 2 && (
          <Paper sx={{ p: 3, ...cardSx }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6" fontWeight={700} sx={{ color: colors.text }}>
                {i18n.t("dashboard.tabs.attendants")}
              </Typography>
              
              <IconButton 
                onClick={exportarGridParaExcel} 
                color="primary"
                size="small"
                sx={{ 
                  bgcolor: "rgba(53, 152, 220, 0.1)",
                  "&:hover": {
                    bgcolor: "rgba(53, 152, 220, 0.2)"
                  }
                }}
              >
                <SaveAlt />
              </IconButton>
            </Box>
            <Divider sx={{ mb: 3 }} />

            <div id="grid-attendants">
              {attendants.length > 0 && (
                <TableAttendantsStatus 
                  attendants={attendants} 
                  loading={loading} 
                />
              )}
            </div>

            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ color: colors.text }}>
                {i18n.t("dashboard.charts.userPerformance")}
              </Typography>
              <ChatsUser />
            </Box>
          </Paper>
        )}
      </Container>
    </Box>
  );
};

export default Dashboard;